import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";
import { cn } from "@/lib/utils/cn";

type SortDropdownProps = {
  descending: boolean;
  onDescendingChange: (descending: boolean) => void;
  className?: string;
};

export function SortDropdown({
  descending,
  onDescendingChange,
  className,
}: SortDropdownProps) {
  const { t } = useTranslation();
  const directions = [
    { descending: false, label: t("common.aToZ") },
    { descending: true, label: t("common.zToA") },
  ];
  const selected = directions.find(
    (direction) => direction.descending === descending,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={`${t("common.order")}: ${selected?.label ?? ""}`}
          className={cn("group", className)}
        >
          <span className="truncate font-semibold">{selected?.label}</span>
          <ChevronDown
            aria-hidden
            className="transition-transform group-data-[state=open]:rotate-180"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{t("common.order")}</DropdownMenuLabel>
        {directions.map((direction) => (
          <SortDropdownItem
            key={direction.label}
            label={direction.label}
            isSelected={direction.descending === descending}
            onSelect={() => onDescendingChange(direction.descending)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type SortDropdownItemProps = {
  label: string;
  isSelected: boolean;
  onSelect: () => void;
};

function SortDropdownItem({
  label,
  isSelected,
  onSelect,
}: SortDropdownItemProps) {
  return (
    <DropdownMenuItem
      role="menuitemradio"
      aria-checked={isSelected}
      onSelect={onSelect}
    >
      <Check
        aria-hidden
        className={cn("text-primary", !isSelected && "invisible")}
      />
      {label}
    </DropdownMenuItem>
  );
}
